import { useEffect, useState } from "react";

import { useT } from "../i18n/index.js";
import { reloadApp } from "../lib/host";
import { CONFIRM_BTN } from "../ui/classes";

interface IdleReconnectDialogProps {
  /**
   * Wall-clock deadline (ms epoch) at which the human-idle kill fires. Non-null
   * → the warning/countdown is showing; null + `killed` → the terminal state.
   */
  readonly endsAt: number | null;
  /** The kill already fired: the session was hard-closed for inactivity. */
  readonly killed: boolean;
  /** "I'm still here" — cancel the pending kill and keep the live session. */
  readonly onStay: () => void;
  /**
   * Killed-mode restart. Surfaces with a persisted per-project session binding
   * MUST clear it here before reloading — a bare reload re-adopts the same
   * discarded, sandbox-less session and resurrects its dead timeline instead
   * of cold-booting a fresh one. Defaults to a plain reload (`reloadApp`: the
   * page on the web, a fresh mount in the Focus panel) for surfaces with no
   * binding (e.g. ImplementFlow's per-run sessions).
   */
  readonly onRestart?: () => void;
}

/** mm:ss for a non-negative second count. */
const formatClock = (totalSeconds: number): string => {
  const s = Math.max(0, totalSeconds);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

/**
 * Human-idle dialog. After a stretch with no human activity we warn with a live
 * countdown; if it elapses we hard-close the session so the server cancels the
 * per-session CI job and frees the sandbox runner (a held socket alone never
 * does — the heartbeat keeps the server reaper from firing). Any gesture, or the
 * "I'm still here" button, cancels the kill. See `useWsSession`'s
 * `idleCountdownEndsAt` / `idle_killed`.
 */
export function IdleReconnectDialog({
  endsAt,
  killed,
  onStay,
  onRestart,
}: IdleReconnectDialogProps) {
  const t = useT();
  // Re-render once a second while counting down so the clock ticks.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (endsAt === null || killed) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endsAt, killed]);

  const secondsLeft =
    endsAt === null ? 0 : Math.ceil((endsAt - now) / 1000);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-4"
    >
      <div className="w-full max-w-sm rounded-lg bg-surface p-5 shadow-xl shadow-elev">
        <h2
          id="idle-dialog-title"
          className="text-base font-semibold text-ink"
        >
          {killed ? t("chatTop.idle.killedTitle") : t("chatTop.idle.title")}
        </h2>
        {killed ? (
          <>
            <p className="mt-2 text-sm text-ink-muted">
              {t("chatTop.idle.killedBody")}
            </p>
            <button
              type="button"
              onClick={onRestart ?? reloadApp}
              className={`mt-4 w-full ${CONFIRM_BTN}`}
            >
              {t("chatTop.idle.restart")}
            </button>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-ink-muted">
              {t("chatTop.idle.body")}
            </p>
            <p
              className="mt-3 text-center text-2xl font-semibold tabular-nums text-ink"
              aria-live="polite"
            >
              {formatClock(secondsLeft)}
            </p>
            <button
              type="button"
              onClick={onStay}
              className={`mt-4 w-full ${CONFIRM_BTN}`}
            >
              {t("chatTop.idle.stay")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
